import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Router } from '@angular/router';
import { USER } from 'src/app/core/user.service';
import {
  LogMode,
  LogStatus,
} from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { FlightLogHttpClient } from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.service';
import { getOrElse } from 'src/util/values';
import { FlightLogViewModel } from 'src/view-models/flight-log-view-model';
import { UserViewModel } from 'src/view-models/user-view-model';

interface ActionButton {
  text: string;
  visible: boolean;
  icon?: string;
  routerLink?: any;
  click?: () => void;
}

@Component({
  selector: 'fl-flight-log-action-buttons',
  templateUrl: './flight-log-action-buttons.component.html',
  styleUrls: ['./flight-log-action-buttons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlightLogActionsComponent implements OnInit, OnChanges {
  @Input()
  public flightLog: FlightLogViewModel;

  @Input()
  public view = false;
  @Input()
  public reopen: boolean | null = null;
  @Input()
  public edit: boolean | null = null;
  @Input()
  public complete: boolean | null = null;
  @Input()
  public delete: boolean | null = null;
  @Input()
  public restore: boolean | null = null;
  @Output()
  public flightLogDeleted = new EventEmitter();
  @Output()
  public flightLogRestored = new EventEmitter();

  public activeButtons: ActionButton[];
  public allButtons: { [id: string]: ActionButton };

  constructor(
    @Inject(USER) private readonly _user: UserViewModel,
    private router: Router,
    private snackBar: MatSnackBar,
    private flightLogs: FlightLogHttpClient,
  ) {}

  public ngOnInit(): void {}

  public ngOnChanges(): void {
    this.initButtons();
  }

  public initButtons() {
    const singleClick = (fn: () => Promise<any>) => {
      let executing = false;
      return async () => {
        if (!executing) {
          try {
            executing = true;
            const result = await fn();
            return result;
          } finally {
            executing = false;
          }
        }
      };
    };

    this.allButtons = {
      view: {
        text: 'View',
        icon: 'assignment',
        visible: this.showView,
        routerLink: this.viewLink,
      },
      edit: {
        text: 'Edit',
        icon: 'mode_edit',
        visible: this.showEdit,
        routerLink: this.editLink,
      },
      beginPerFlight: {
        text: 'Begin Per Flight',
        icon: 'assignment',
        visible: this.showBeginPerFlight,
        click: singleClick(() => this.beginFlight(LogMode.EntryPerFlight)),
      },
      beginPerLeg: {
        text: 'Begin Per Leg',
        icon: 'assignment',
        visible: this.showBeginPerLeg,
        click: singleClick(() => this.beginFlight(LogMode.EntryPerLeg)),
      },
      reopen: {
        text: 'Re-Open',
        icon: 'undo',
        visible: this.showReopen,
        click: singleClick(() => this.reopenFlight()),
      },
      delete: {
        text: 'Delete',
        icon: 'delete',
        visible: this.showDelete,
        click: singleClick(() => this.deleteFlight()),
      },
      restore: {
        text: 'Restore',
        icon: 'replay',
        visible: this.showRestore,
        click: singleClick(() => this.restoreFlight()),
      },
    };

    this.activeButtons = Object.getOwnPropertyNames(this.allButtons)
      .filter((prop) => this.allButtons[prop].visible)
      .map((prop) => this.allButtons[prop]);
  }

  get status() {
    return this.flightLog.status;
  }

  get isDeleted(): boolean {
    return this.flightLog.deletedAt != null && this.flightLog.deletedAt !== undefined;
  }

  get showView() {
    return this.view;
  }

  get showEdit(): boolean {
    return getOrElse(
      this.edit,
      this.statusIs(LogStatus.Draft, LogStatus.ReOpened) && !this.isDeleted,
    );
  }

  get showDelete(): boolean {
    return getOrElse(this.delete, !this.statusIs(LogStatus.Complete) && !this.isDeleted);
  }

  get showRestore(): boolean {
    const defaultRestore = this._user.isAviationSupervisor && this.isDeleted;
    return getOrElse(this.restore, defaultRestore);
  }

  get showBeginPerFlight() {
    const begin = this.statusIs(LogStatus.NotStarted) && !this.isDeleted;
    const canShow = this._user.flightLogFeatures.perFlightLogging;
    return canShow ? begin : false;
  }

  get showBeginPerLeg() {
    const begin = this.statusIs(LogStatus.NotStarted) && !this.isDeleted;
    const canShow = this._user.flightLogFeatures.perLegLogging;
    return canShow ? begin : false;
  }

  get showReopen() {
    const defaultReOpen = this._user.isAviationSupervisor && this.statusIs(LogStatus.Complete);
    return getOrElse(this.reopen, defaultReOpen);
  }

  get showActionsIcon() {
    return (
      this.activeButtons.length > 1 &&
      (this.showView ||
        this.showEdit ||
        this.showBeginPerLeg ||
        this.showBeginPerFlight ||
        this.showReopen)
    );
  }

  public statusIs(...status: LogStatus[]) {
    return status.includes(this.status);
  }

  get editLink() {
    return `/flight-logs/${this.flightLog.id}/edit`;
  }

  get viewLink() {
    const viewSummaryUrl = `/flight-logs/${this.flightLog.id}`;
    const editReadOnlyUrl = `/flight-logs/${this.flightLog.id}/edit`;
    return this.statusIs(LogStatus.Complete) ? editReadOnlyUrl : viewSummaryUrl;
  }

  get restoreLink() {
    return `/flight-logs/${this.flightLog.id}/restore`;
  }

  public async deleteFlight() {
    try {
      await this.flightLogs.delete(this.flightLog.id).toPromise();
      this.snackBar.open('Success: Flight Log deleted.', undefined, {
        duration: 3000,
      });

      this.flightLogDeleted.emit();
    } catch (err) {
      this.snackBar.open('There was an error deleting the flight log.', undefined, {
        duration: 3000,
      });
    }
  }

  public async restoreFlight() {
    try {
      await this.flightLogs.restore(this.flightLog.id).toPromise();

      this.snackBar.open('Success: Flight Log restored.', undefined, {
        duration: 3000,
      });

      this.flightLogRestored.emit();
    } catch (err) {
      this.snackBar.open('There was an error restoring the flight log.', undefined, {
        duration: 3000,
      });
    }
  }

  public async reopenFlight() {
    try {
      await this.flightLogs.reopen(this.flightLog.id).toPromise();
      await this.router.navigate([this.editLink]);
      this.snackBar.open('Success: Flight Log re-opened for editing.', undefined, {
        duration: 3000,
      });
    } catch (err) {
      this.snackBar.open('There was an error re-opening the flight log.', undefined, {
        duration: 3000,
      });
    }
  }

  public async beginFlight(mode: LogMode) {
    const fsId = this.flightLog.flightSummaryId;
    const sm = this.snackBar.open(`Starting log for flight ${fsId}...`, undefined, {
      duration: 3000,
    });
    try {
      await this.flightLogs.begin(this.flightLog.id, mode).toPromise();
      await this.router.navigate([this.editLink]);
      sm.dismiss();
      this.snackBar.open(`Success: started log for flight ${fsId}.`, undefined, {
        duration: 3000,
      });
    } catch (err) {
      sm.dismiss();
      const error = `There was an error starting the log for flight ${fsId}. `;
      this.snackBar.open(error, undefined, { duration: 3000 });
    }
  }

  public onClick(event: Event) {
    // We stop event propogation so when this is used in a
    // coavalent clickable data table, the rowClicked
    // event is not triggered
    event.stopPropagation();
  }
}
